import {Chip, Switch} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {Icon} from "@iconify/react";
import {addToast} from "@heroui/toast";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React, {useEffect, useRef, useState} from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import {SPORTS_LIST} from "@/user/_components/useEntityEditor.tsx";
import Header from "../../../Header.tsx";
import TrialConversionsChart from "../../../master/TrialConversionsChart.tsx";
import ReturnLeaderboard from "../../../master/ReturnLeaderboard.tsx";
import WagerPilotClubs from "../../../master/WagerPilotClubs.tsx";
import OneOffFunnel from "../../../master/OneOffFunnel.tsx";
import UserEventPasses from "../../../master/UserEventPasses.tsx";
import UserCustomPricing from "../../../master/UserCustomPricing.tsx";
import PassPricesAndTrial from "../../../master/PassPricesAndTrial.tsx";
import TransactionFees from "../../../master/TransactionFees.tsx";
import {SignInAsButton} from "../../../components/ImpersonationBanner.tsx";
import GiveEventPass from "../../../master/GiveEventPass.tsx";
import SignInAsSearch from "../../../master/SignInAsSearch.tsx";
import WhatsAppSessionsTable from "../../../master/WhatsAppSessionsTable.tsx";
import WhatsAppSessionsCosts from "../../../master/WhatsAppSessionsCosts.tsx";
import ActivationDashboard from "../../../master/ActivationDashboard.tsx";
import TrialPassLog from '../../../master/TrialPassLog';
import PlayersActivationTable from "../../../master/PlayersActivationTable.tsx";

export const Route = createFileRoute('/_app/master/activation')({
    component: RouteComponent,
})

// Mirrors MasterController::activation — every real signup in the window and how far each got.
type ActivationRow = {
    uid: string;
    name: string;
    created_at: string;
    role: number | null;
    source: 'quiz' | 'invited' | 'self';
    via_google: boolean;
    /** Quiz signups: the way through the quiz and where it ended (MasterController::quizRoute). */
    quiz_route?: string | null;
    /** Players: who referred them and the club they joined through it. */
    referral: {by: string | null; club: string | null} | null;
    /** Organizers: what they signed up as. */
    club_type: 'club' | 'community' | 'team' | null;
    country_code: string | null;
    phone_verified: boolean;
    events_created: number;
    /** Their entrant records — applications and team slots — whoever put them there. */
    events_joined: number;
    /** The subset they submitted themselves: public register page, Join modal, WhatsApp. */
    self_joined: number;
    played: boolean;
    event_scored: boolean;
    last_active_at: string | null;
    returned: boolean;
    // Signup questionnaire (club organizers only): the court-booking-module answer.
    court_booking: 'yes' | 'no' | null;
    court_booking_details: string | null;
    // Their latest event (sport falls back to the signup choice when they've built nothing).
    sport: string | null;
    event_kind: 'tournament' | 'league' | null;
    event_type: string | null;
    draw_created: boolean | null;
    // Commercial state off their club: the reverse trial's phase and the tier they pay for.
    trial_status: 'none' | 'active' | 'finished' | null;
    trial_ends: string | null;
    trial_started: string | null;
    /** The club's pricing world: legacy, or model 2 on the community or one-off path. */
    organizer_path: 'legacy' | 'community' | 'one_off' | null;
    /** Their latest event's field, counted as the one-off path counts it: teams for a team sport. */
    entrants: number | null;
    entrant_unit: 'players' | 'teams' | null;
    /** Event Passes held, by how they came by them. */
    passes: {paid: number; paid_cents: number; currency: string | null; earned: number; granted: number} | null;
    paid_tier: string | null;
    /** Someone paid an entry fee online via their registration form (Stripe checkout). */
    reg_payments: boolean;
    // Master-only: for pasting into the Pricing Lab, which resolves by email or user uid.
    email: string | null;
};

/**
 * The user's name, expandable to their email and uid. The two IDs exist to be pasted into the
 * Pricing Lab, so each is click-to-copy rather than merely displayed.
 *
 * Open state lives with the table: opening a user also opens their Event Passes row underneath.
 */
function UserCell({row, open, onToggle}: {row: ActivationRow; open: boolean; onToggle: () => void}) {
    const [copied, setCopied] = useState<string | null>(null);
    const copy = (label: string, value: string) => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(label);
            setTimeout(() => setCopied(null), 1200);
        });
    };
    return (
        <div className="max-w-[220px]">
            <button
                onClick={onToggle}
                aria-expanded={open}
                title={open ? undefined : 'Show email, user ID and Event Passes'}
                className="block max-w-full truncate text-left font-medium hover:text-primary"
            >
                {row.name || '—'}
            </button>
            {open && (
                <div className="mt-0.5 space-y-0.5 text-[11px]">
                    {row.email && (
                        <button onClick={() => copy('email', row.email!)} title="Copy email"
                                className="block max-w-full truncate text-left text-default-500 hover:text-primary">
                            {copied === 'email' ? 'copied' : row.email}
                        </button>
                    )}
                    <button onClick={() => copy('uid', row.uid)} title="Copy user ID"
                            className="block max-w-full truncate text-left font-mono text-default-400 hover:text-primary">
                        {copied === 'uid' ? 'copied' : row.uid}
                    </button>
                </div>
            )}
        </div>
    );
}

// 4 = EmptyAfterSignup: the Google/Discord sign-in made the account, and they left before choosing player or organizer.
const ROLE_LABELS: Record<number, string> = {1: 'Player', 2: 'Organizer', 4: 'Google, unfinished'};
const CLUB_TYPE_LABELS: Record<string, string> = {club: 'Club', community: 'Community', team: 'Team'};
const WINDOWS = [7, 30, 90, 365];

/** "2026-09-03" → "3 Sep". UTC-parsed: the API sends a plain date, and parsing it as a local
 *  instant would shift it a day either side of midnight. */
function shortDate(date: string): string {
    const t = Date.parse(date + 'T00:00:00Z');
    if (Number.isNaN(t)) return date;
    return new Date(t).toLocaleDateString(undefined, {day: 'numeric', month: 'short', timeZone: 'UTC'});
}

/**
 * Live platform switches (master-only, backed by app settings — no deploy to flip):
 * the one-off events paywall and the 8-entry Pay-as-you-go cap.
 */
function PlatformSwitches() {
    const queryClient = useQueryClient();
    const [saving, setSaving] = useState<string | null>(null);
    // Draft of the soft-launch club list; null = not edited, show the saved value.

    const [numberDraft, setNumberDraft] = useState<string | null>(null);
    const settingsQuery = useQuery<{one_off_events_free: boolean; entry_cap_disabled: boolean; entitlements_enforced: boolean; one_off_path_enabled: boolean; golive_gate_enabled?: boolean; event_pass_waived?: boolean; trial_v3_enabled?: boolean; build_quiz_enabled?: boolean; league_home_v2_enabled?: boolean; share_results_enabled?: boolean; player_results_enabled?: boolean; payments_v3_enabled?: boolean; plus_trial_enabled?: boolean; plus_trial_email_enabled?: boolean; pass_offer_enabled?: boolean; pass_followup_email_enabled?: boolean; multi_sport_enabled?: boolean; football_v2_enabled?: boolean; event_quiz_enabled?: boolean; whatsapp_score_bot_enabled?: boolean; whatsapp_registrations_enabled?: boolean; whatsapp_sessions_trial_enabled?: boolean; whatsapp_sessions_trial_enabled_at?: string | null; friendly_wagers_enabled?: boolean; support_whatsapp_number?: string}>({
        queryKey: ['master-app-settings'],
        queryFn:  () => apiRetrieveImproved('app-settings-get' as any, {}),
    });

    const setSwitch = async (key: string, value: boolean) => {
        setSaving(key);
        // Optimistic — the switch reflects the click immediately, the server confirms.
        queryClient.setQueryData(['master-app-settings'], (s: any) => ({...s, [key]: value}));
        try {
            await apiActionImproved('app-settings-set' as any, {key, value});
            addToast({title: 'Setting saved — live immediately', color: 'success'});
        } catch (e: any) {
            addToast({title: e?.message || 'Could not save setting', color: 'danger'});
            queryClient.invalidateQueries({queryKey: ['master-app-settings']});
        } finally {
            setSaving(null);
        }
    };

    const s = settingsQuery.data;

    return (
        <div className="mb-6 rounded-xl border border-default-200 bg-content1 p-4">
            <h2 className="text-sm font-bold uppercase tracking-wide mb-1">Platform switches</h2>
            <p className="text-xs text-default-500 mb-3">Take effect immediately for everyone — no deploy.</p>
            {!s ? <Spinner size="sm" /> : (
                <div className="flex flex-col gap-3">
                    <Switch
                        isSelected={s.one_off_events_free}
                        isDisabled={saving === 'one_off_events_free'}
                        onValueChange={(v) => setSwitch('one_off_events_free', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">One-off events paywall OFF</div>
                            <div className="text-xs text-default-500">
                                On: americano, mexicano, KOTC, ryder cup and clashes create free (ad-supported).
                                Off: the paid options come back.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={s.entitlements_enforced}
                        isDisabled={saving === 'entitlements_enforced'}
                        onValueChange={(v) => setSwitch('entitlements_enforced', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Entitlements enforced (new pricing model)</div>
                            <div className="text-xs text-default-500">
                                On: clubs on pricing model 2 are gated by their tier — Match Centre, Club Players,
                                leagues and the rest. Legacy clubs are never affected either way.
                                Off: every club behaves exactly as it does today.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={s.one_off_path_enabled}
                        isDisabled={saving === 'one_off_path_enabled'}
                        onValueChange={(v) => setSwitch('one_off_path_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">One-off organizer path (sized Event Pass at go-live)</div>
                            <div className="text-xs text-default-500">
                                On: model-2 clubs on the one-off path pay inside their first event — free up to 8 entries,
                                then a pass sized by entry count before the first score (the draw always builds), or the earn route.
                                Needs "Entitlements enforced" on as well. Community and legacy clubs are never affected.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.golive_gate_enabled}
                        isDisabled={saving === 'golive_gate_enabled'}
                        onValueChange={(v) => setSwitch('golive_gate_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Go-live gate (free up to 8 / 4, sized pass, enforced at the first live action)</div>
                            <div className="text-xs text-default-500">
                                On: every model-2 club runs tournaments free up to 8 entries and leagues up to 4 (teams for team sports,
                                players for racquet sports); above that the price is shown at the draw and the sized Event Pass
                                ($9.99 / $19.99 / $29.99) is asked for at the first live action — go live, PDF, score. Registration is never gated.
                                A trial never lifts the cap; a used pass or a paid plan does. The draw always builds. Needs "Entitlements
                                enforced" on; supersedes the one-off path switch, which stays off. Off: today's behaviour.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.event_pass_waived}
                        isDisabled={saving === 'event_pass_waived'}
                        onValueChange={(v) => setSwitch('event_pass_waived', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">No Event Pass required (every tournament and league runs free)</div>
                            <div className="text-xs text-default-500">
                                On: no event owes an Event Pass, whatever its size — no price at the draw, no pass asked at any result,
                                no pass banner, email or offer, and Billing stops selling passes. Only the Plus features stay behind the
                                plans and the Plus trial. Leave "Go-live gate" ON with it: switching the gate off instead brings back the
                                old trial locks and paused leagues. Off: the gate as it is.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.build_quiz_enabled}
                        isDisabled={saving === 'build_quiz_enabled'}
                        onValueChange={(v) => setSwitch('build_quiz_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">/build/ quiz (draw on screen, share link, sign-up last)</div>
                            <div className="text-xs text-default-500">
                                On: /build/ asks four questions, shows the real draw before sign-up, gives the share link, and the
                                sign-up claims the event. Runs beside /quiz/, which is unchanged; site links stay on /quiz/. Unclaimed
                                provisional events are purged after 7 days. Off: /build/ hands visitors to /quiz/ with the same query string.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.league_home_v2_enabled}
                        isDisabled={saving === 'league_home_v2_enabled'}
                        onValueChange={(v) => setSwitch('league_home_v2_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">League Home tab (organiser overview)</div>
                            <div className="text-xs text-default-500">
                                On: a league's Home tab is the organiser's League Hub — status, teams, fixtures and progress, the next
                                matchday, the top of the table, registrations needing attention and the share links — matching the
                                tournament Home. Off: the Home tab shows the public page, as today. Public pages, Live View, TV Mode and
                                Match Centre are the same either way.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.share_results_enabled}
                        isDisabled={saving === 'share_results_enabled'}
                        onValueChange={(v) => setSwitch('share_results_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Share the results (completion screen, results card, public results page)</div>
                            <div className="text-xs text-default-500">
                                On: finishing a tournament or league opens "Share the results" — the champion's road to victory, the
                                podium, a WhatsApp message and a results card image; a finished event's Home tab carries it as a card;
                                the public page of a finished event opens on the results with share tags for WhatsApp and Instagram.
                                Off: Finish behaves exactly as today and the results endpoints answer 404.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.player_results_enabled}
                        isDisabled={saving === 'player_results_enabled'}
                        onValueChange={(v) => setSwitch('player_results_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Players enter results (Open games on the player&rsquo;s profile)</div>
                            <div className="text-xs text-default-500">
                                On: league and tournament setup gets a "Players enter results" switch. Where an organiser turns it on,
                                each player sees their unfinished games under Open games on their profile and enters the score full
                                screen, with the side they played on, the venue and the time; the game&rsquo;s players get an email and a
                                push, everyone else in the event a push. Where the event&rsquo;s switch is off (every event until its
                                organiser turns it on, ladders included), players cannot score their games by any route — the game
                                page, the WhatsApp score bot or Discord — and only the organiser and admins enter results. Esports
                                keeps its own report-and-confirm. Off: players score their own games exactly as today, there is no
                                event switch and no Open games, and the endpoints answer 404.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.payments_v3_enabled}
                        isDisabled={saving === 'payments_v3_enabled'}
                        onValueChange={(v) => setSwitch('payments_v3_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Payments v3 (Express accounts, direct charges, all-in fees, collect on the day)</div>
                            <div className="text-xs text-default-500">
                                On: a new "Connect Stripe" makes an Express account where the country is offered one and the
                                entry fee is a direct charge on the organiser's account carrying the all-in fee (3% + 40p, 4.5% +
                                $0.80 …, Plus a point lower); elsewhere the Payments step says "Collect on the day" and the Players
                                tab tracks who has paid. Accounts connected before stay exactly as they are. Off: today's accounts,
                                charges and fees. The Connect webhook secret must be set before this goes on.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.plus_trial_enabled}
                        isDisabled={saving === 'plus_trial_enabled'}
                        onValueChange={(v) => setSwitch('plus_trial_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">$2.99 Plus trial (14 days, everything unlocked, then $39/month)</div>
                            <div className="text-xs text-default-500">
                                On: the go-live modal, Billing and the sidebar offer the trial — card required, the size cap lifted
                                for the trial, one per club, card and mailbox, a reminder three days before the end and a one-click
                                cancel. Off: never offered; a running trial is unaffected.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.plus_trial_email_enabled}
                        isDisabled={saving === 'plus_trial_email_enabled'}
                        onValueChange={(v) => setSwitch('plus_trial_email_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Plus trial email ("Everything unlocked for 14 days — $2.99")</div>
                            <div className="text-xs text-default-500">
                                On: every new club is emailed the pitch a little after it is created (whatever route made it), and
                                Master → Campaigns gets a "Plus trial email" entry to send it by hand to a filtered audience. Once
                                per club, ever; never to legacy clubs, clubs on a plan, clubs that have had the trial, or clubs made
                                in a sign-in-as session. Off: never sent, and the Campaigns entry is hidden.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.pass_offer_enabled}
                        isDisabled={saving === 'pass_offer_enabled'}
                        onValueChange={(v) => setSwitch('pass_offer_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">50% Event Pass offer (a personal code, 72 hours, one per club)</div>
                            <div className="text-xs text-default-500">
                                On: 2 hours after a club first sees the pass modal and buys nothing it is emailed a code for 50% off
                                one Event Pass of any size, with a reminder at 48 hours; the pass modal and Billing show a "Have a
                                code?" box; Master → Campaigns → Features gets a "50% pass offer" entry for hand sends. Never to
                                legacy clubs, clubs on a plan, clubs holding a pass, clubs with a trial started, or clubs made in
                                a sign-in-as session. Off: nothing sent, no code box, no Campaigns entry. Separate from the Plus
                                trial email; the two never mention each other.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.pass_followup_email_enabled}
                        isDisabled={saving === 'pass_followup_email_enabled'}
                        onValueChange={(v) => setSwitch('pass_followup_email_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Event Pass follow-up email ("Quick question about …", an hour after the pass modal)</div>
                            <div className="text-xs text-default-500">
                                On: an hour after an organiser first sees the pass modal on an event and buys nothing, Shaun emails
                                them a Continue button back into that event's pass checkout, a money-back guarantee (refunds by hand,
                                no discount) and four one-click reasons for not going ahead. Once per event, ever; never to a finished
                                event, an event holding a pass, a club that needs none, or an unsubscribed organiser. The reasons and
                                whether a pass was bought later land on Master → Event Pass feedback. Off: nothing sent.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.trial_v3_enabled}
                        isDisabled={saving === 'trial_v3_enabled'}
                        onValueChange={(v) => setSwitch('trial_v3_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Trial v3 (48 hours on the one-off route, matchdays for a league)</div>
                            <div className="text-xs text-default-500">
                                On: a one-off-path club's trial of Plus features is 48 hours from its start; a club whose first event
                                is a league keeps Plus features until the earlier of 3 completed matchdays or 21 days from the first;
                                the community route is unchanged. Warnings: one at +24h (one-off), one at matchday 2 (league). A trial
                                never lifts the size cap either way. Run trials:recompute --apply before flipping. Off: the 7-day engine as it is.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={s.entry_cap_disabled}
                        isDisabled={saving === 'entry_cap_disabled'}
                        onValueChange={(v) => setSwitch('entry_cap_disabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">8-entry free cap OFF</div>
                            <div className="text-xs text-default-500">
                                On: free-tier events take unlimited entries and draws never lock.
                                Off: the Pay-as-you-go cap of 8 applies as normal.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.multi_sport_enabled}
                        isDisabled={saving === 'multi_sport_enabled'}
                        onValueChange={(v) => setSwitch('multi_sport_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Multi-sport</div>
                            <div className="text-xs text-default-500">
                                On: the new sports, their scorecards and the new sport picker are live.
                                Off: today's sports behave exactly as they do now and nothing new is offered.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.football_v2_enabled}
                        isDisabled={saving === 'football_v2_enabled' || (!s.multi_sport_enabled && !s.football_v2_enabled)}
                        onValueChange={(v) => setSwitch('football_v2_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Football results (football v2)</div>
                            <div className="text-xs text-default-500">
                                On: a football result is full time, then extra time and penalties only when a knockout is level,
                                with walkovers, awarded, abandoned and postponed games. Needs Multi-sport on.
                                Off: football scores are entered and read exactly as they are now.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.event_quiz_enabled}
                        isDisabled={saving === 'event_quiz_enabled'}
                        onValueChange={(v) => setSwitch('event_quiz_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Event quiz v2 (/event-quiz/)</div>
                            <div className="text-xs text-default-500">
                                On: /event-quiz/ records reports and signs organizers up, creating one-off events with their players.
                                Off: the page and its endpoints are not offered. /quiz and /quiz-community are unaffected either way.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.whatsapp_score_bot_enabled}
                        isDisabled={saving === 'whatsapp_score_bot_enabled'}
                        onValueChange={(v) => setSwitch('whatsapp_score_bot_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">WhatsApp score bot</div>
                            <div className="text-xs text-default-500">
                                On: players can text a score to the WhatsApp number from a verified phone.
                                Off: messages to the score bot are ignored.
                            </div>
                        </div>
                    </Switch>
                    <Switch
                        isSelected={!!s.whatsapp_registrations_enabled}
                        isDisabled={saving === 'whatsapp_registrations_enabled'}
                        onValueChange={(v) => setSwitch('whatsapp_registrations_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">WhatsApp registrations</div>
                            <div className="text-xs text-default-500">
                                On: organizers whose plan includes it set up sessions in a league, and players sign up
                                in a chat with the bot (the score bot has to be on too). Off: the section is hidden and
                                sign-up codes are answered &ldquo;not available&rdquo;.
                            </div>
                        </div>
                    </Switch>

                    <Switch
                        isSelected={!!s.whatsapp_sessions_trial_enabled}
                        isDisabled={saving === 'whatsapp_sessions_trial_enabled'}
                        onValueChange={(v) => setSwitch('whatsapp_sessions_trial_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">WhatsApp Sessions trial</div>
                            <div className="text-xs text-default-500">
                                On: WhatsApp Sessions is sold at Plus — only a PAID Plus counts, not a club holding Plus
                                before or on its trial — and a club without it gets one free session of up to 20 places,
                                gated when it publishes (the link and the post), never while a session runs. Organizers
                                see the welcome once, the dashboard card and NEW in the sidebar (60 days
                                {s.whatsapp_sessions_trial_enabled_at ? ` from ${new Date(s.whatsapp_sessions_trial_enabled_at).toLocaleDateString()}` : ''}).
                                Off: sessions are gated as before, on the plan, at every call.
                            </div>
                        </div>
                    </Switch>

                    <Switch
                        isSelected={!!s.friendly_wagers_enabled}
                        isDisabled={saving === 'friendly_wagers_enabled'}
                        onValueChange={(v) => setSwitch('friendly_wagers_enabled', v)}
                    >
                        <div>
                            <div className="text-sm font-medium">Friendly Wagers</div>
                            <div className="text-xs text-default-500">
                                On: players in clubs whose plan includes it get free Tip Coins each season to tip on the
                                club&rsquo;s upcoming games. Tip Coins are play money, separate from earned coins.
                                Off: every wager surface is hidden and its endpoints refuse, except for the pilot clubs below.
                            </div>
                        </div>
                    </Switch>
                    <WagerPilotClubs switchOn={!!s.friendly_wagers_enabled} />

                    {/* The WhatsApp Business Platform number behind the portal's floating
                        "Chat with us" button. Empty hides the button for everyone. */}
                    <div className="ml-1 border-t border-default-100 pt-3">
                        <div className="text-sm font-medium">Support WhatsApp number</div>
                        <div className="text-xs text-default-500 mb-1.5">
                            Signed-in users get a floating WhatsApp button that opens a chat with this number,
                            pre-filled with who they are. Country code included, e.g. +44 7700 900123. Leave empty
                            to hide the button.
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                value={numberDraft ?? s.support_whatsapp_number ?? ''}
                                onChange={(e) => setNumberDraft(e.target.value)}
                                placeholder="+44 7700 900123"
                                spellCheck={false}
                                className="w-full max-w-xs rounded-lg border border-default-200 bg-content2 px-3 py-1.5 font-mono text-xs outline-none focus:border-primary"
                            />
                            <button
                                type="button"
                                disabled={numberDraft === null || saving === 'support_whatsapp_number'}
                                onClick={async () => {
                                    setSaving('support_whatsapp_number');
                                    try {
                                        const res: any = await apiActionImproved('app-settings-set' as any, {key: 'support_whatsapp_number', value: numberDraft ?? ''});
                                        addToast({title: res?.value ? `Support number saved: +${res.value}` : 'Support number cleared — button hidden', color: 'success'});
                                        setNumberDraft(null);
                                    } catch (e: any) {
                                        addToast({title: e?.message || 'Could not save the number', color: 'danger'});
                                    } finally {
                                        setSaving(null);
                                        queryClient.invalidateQueries({queryKey: ['master-app-settings']});
                                    }
                                }}
                                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                            >
                                Save
                            </button>
                            {s.support_whatsapp_number && numberDraft === null && (
                                <a href={`https://wa.me/${s.support_whatsapp_number}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                                    Test link
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * A wide table with a second horizontal scrollbar above it, kept in sync with the real one —
 * so the columns can be scrolled without first scrolling to the bottom of a long table.
 */
function DualScrollTable({children}: {children: React.ReactNode}) {
    const topRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(0);
    // Guards the two onScroll handlers from bouncing off each other.
    const syncing = useRef(false);

    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return;
        const measure = () => setWidth(body.scrollWidth);
        measure();
        // Observing both the viewport and the table itself covers window resizes and any
        // change in row content that widens a column.
        const ro = new ResizeObserver(measure);
        ro.observe(body);
        if (body.firstElementChild) ro.observe(body.firstElementChild);
        return () => ro.disconnect();
    }, []);

    const mirror = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
        if (!from || !to || syncing.current) return;
        syncing.current = true;
        to.scrollLeft = from.scrollLeft;
        requestAnimationFrame(() => { syncing.current = false; });
    };

    return (
        <>
            <div
                ref={topRef}
                onScroll={() => mirror(topRef.current, bodyRef.current)}
                className="overflow-x-auto overflow-y-hidden rounded-t-xl border border-b-0 border-default-200 bg-default-50"
                aria-hidden="true"
            >
                <div style={{width, height: 1}} />
            </div>
            <div
                ref={bodyRef}
                onScroll={() => mirror(bodyRef.current, topRef.current)}
                className="overflow-x-auto rounded-b-xl border border-default-200"
            >
                {children}
            </div>
        </>
    );
}

function StatCard({label, value, sub}: {label: string; value: React.ReactNode; sub?: string}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-sm text-default-500">{label}</div>
            {sub && <div className="text-xs text-default-400">{sub}</div>}
        </div>
    );
}

const dash = <span className="text-default-300">—</span>;
const dayMonth = (iso: string) => new Date(iso).toLocaleDateString(undefined, {day: '2-digit', month: 'short'});
const yesChip = (label = 'Yes') => <Chip color="success" size="sm" variant="flat">{label}</Chip>;

/** Whole days from today (UTC) to a plain "2026-10-02" date; negative once it has passed. */
function daysUntil(date: string): number {
    const t = Date.parse(date + 'T00:00:00Z');
    const today = Date.parse(new Date().toISOString().slice(0, 10) + 'T00:00:00Z');
    return Math.round((t - today) / 86_400_000);
}

type CellCtx = {openUid: string | null; toggle: (uid: string) => void; freeEntries: number};
type Column = {label: string; cell: (r: ActivationRow, ctx: CellCtx) => React.ReactNode; className?: string};

/**
 * Every column the signups tables can show. Each tab picks the ones that answer its question, so a
 * cell is written once and reads the same in every tab.
 */
const COLS = {
    signedUp: {label: 'Signed up', className: 'whitespace-nowrap text-default-500', cell: (r) => dayMonth(r.created_at)},
    user: {label: 'User', cell: (r, c) => <UserCell row={r} open={c.openUid === r.uid} onToggle={() => c.toggle(r.uid)} />},
    role: {label: 'Role', className: 'whitespace-nowrap', cell: (r) => r.role != null && ROLE_LABELS[r.role]
        ? <>
            <Chip size="sm" variant="flat" color={r.role === 2 ? 'secondary' : r.role === 4 ? 'warning' : 'default'}>{ROLE_LABELS[r.role]}</Chip>
            {/* Organizers: a club (venue) or a community, and whether they asked for court bookings at signup. */}
            {r.role === 2 && r.club_type && <span className="ml-1 text-xs text-default-500">{CLUB_TYPE_LABELS[r.club_type]}</span>}
            {r.role === 2 && r.court_booking === 'yes' && (
                <Chip size="sm" variant="flat" color="success" className="ml-1" title={r.court_booking_details || undefined}>Court bookings</Chip>
            )}
        </>
        : dash},
    source: {label: 'Source', className: 'whitespace-nowrap', cell: (r) => <>
        <Chip size="sm" variant="flat" color={r.source === 'quiz' ? 'warning' : r.source === 'invited' ? 'primary' : 'default'}>{r.source}</Chip>
        {/* "G" = signed up with Google rather than email+password. */}
        {r.via_google && <span className="ml-1 text-xs text-default-400" title="Signed up with Google">G</span>}
        {/* The way through the quiz, and whether an event came out of it. */}
        {r.source === 'quiz' && r.quiz_route && (
            <div className="mt-0.5 max-w-[280px] truncate text-[11px] text-default-500" title={r.quiz_route}>
                {r.quiz_route}
                {r.events_created === 0 && <span className="font-semibold text-warning-700"> · no event set up</span>}
            </div>
        )}
        {/* Players: referred by whom, into which club. */}
        {r.referral && (
            <span className="ml-1 text-xs text-default-500" title={r.referral.by ? `Referred by ${r.referral.by}` : undefined}>
                Referred{r.referral.club ? ` to ${r.referral.club}` : r.referral.by ? ` by ${r.referral.by}` : ''}
            </span>
        )}
    </>},
    phone: {label: 'Phone', cell: (r) => r.phone_verified ? yesChip() : dash},
    // Their latest event: sport, kind, format, and whether the draw/schedule was ever generated.
    sport: {label: 'Sport', className: 'whitespace-nowrap text-default-600',
        cell: (r) => (SPORTS_LIST as Record<string, string>)[r.sport || ''] || r.sport || dash},
    event: {label: 'Event', className: 'whitespace-nowrap', cell: (r) => r.event_kind
        ? <Chip size="sm" variant="flat" color={r.event_kind === 'tournament' ? 'secondary' : 'primary'}>
            {r.event_kind === 'tournament' ? 'Tournament' : 'League'}
        </Chip>
        : dash},
    eventType: {label: 'Event type', className: 'max-w-[150px] truncate text-default-600', cell: (r) => r.event_type || dash},
    draw: {label: 'Draw', cell: (r) => r.draw_created === null ? dash
        : r.draw_created ? yesChip() : <Chip color="warning" size="sm" variant="flat">No</Chip>},
    // Who entered their latest event — teams for a team sport, people otherwise. "Players" and
    // "Self-joined" below are a different thing: the USER's own entrant records.
    entrants: {label: 'Entrants', className: 'whitespace-nowrap tabular-nums', cell: (r) => r.entrants
        ? <>{r.entrants} <span className="text-default-400">{r.entrant_unit === 'teams' ? 'teams' : 'players'}</span></>
        : dash},
    // The one-off path's question: is the event inside the free band, or does it need a pass?
    entrantsVsFree: {label: 'Entrants', className: 'whitespace-nowrap tabular-nums', cell: (r, c) => !r.entrants ? dash : <>
        {r.entrants} <span className="text-default-400">{r.entrant_unit === 'teams' ? 'teams' : 'players'}</span>{' '}
        {c.freeEntries > 0 && r.entrants > c.freeEntries
            ? <Chip color="warning" size="sm" variant="flat">over free {c.freeEntries}</Chip>
            : <Chip color="success" size="sm" variant="flat">free band</Chip>}
    </>},
    created: {label: 'Created', className: 'tabular-nums', cell: (r) => r.events_created > 0 ? r.events_created : dash},
    // Players = every entrant record under the user, an organizer-typed roster included; Self-joined
    // = only the registrations they submitted themselves (origin self). Never one number.
    players: {label: 'Players', className: 'tabular-nums', cell: (r) => r.events_joined > 0 ? r.events_joined : dash},
    selfJoined: {label: 'Self-joined', className: 'tabular-nums', cell: (r) => r.self_joined > 0 ? r.self_joined : dash},
    scores: {label: 'Scores', cell: (r) => (r.played || r.event_scored) ? yesChip(r.event_scored ? 'On event' : 'Played') : dash},
    lastActive: {label: 'Last active', className: 'whitespace-nowrap text-default-500', cell: (r) => r.last_active_at ? dayMonth(r.last_active_at) : '—'},
    cameBack: {label: 'Came back', cell: (r) => r.returned ? yesChip() : <Chip color="warning" size="sm" variant="flat">No</Chip>},
    // Reverse-trial state off their club: running, run out, or never started. The expiry rides ON
    // the chip — it used to live only in the hover title, which made "when does this end" a hunt.
    trial: {label: 'Trial', className: 'whitespace-nowrap', cell: (r) => r.trial_status === 'active'
        ? <Chip color="secondary" size="sm" variant="flat">In trial{r.trial_ends ? ` · until ${shortDate(r.trial_ends)}` : ''}</Chip>
        : r.trial_status === 'finished'
            ? <Chip color="warning" size="sm" variant="flat">Finished{r.trial_ends ? ` ${shortDate(r.trial_ends)}` : ''}</Chip>
            : dash},
    trialStarted: {label: 'Trial started', className: 'whitespace-nowrap text-default-600', cell: (r) => r.trial_started ? shortDate(r.trial_started) : dash},
    trialEnds: {label: 'Trial ends', className: 'whitespace-nowrap font-medium', cell: (r) => r.trial_ends ? shortDate(r.trial_ends) : dash},
    // How long is left — the column to act on: a trial ending in two days wants a call today.
    trialLeft: {label: 'Time left', className: 'whitespace-nowrap', cell: (r) => {
        if (r.trial_status === 'finished') {
            const ago = r.trial_ends ? -daysUntil(r.trial_ends) : null;
            return <Chip color="default" size="sm" variant="flat">Ended{ago != null && ago > 0 ? ` ${ago}d ago` : ''}</Chip>;
        }
        if (!r.trial_ends) return dash;
        const left = daysUntil(r.trial_ends);
        return <Chip size="sm" variant="flat" color={left <= 2 ? 'danger' : left <= 5 ? 'warning' : 'secondary'}>
            {left <= 0 ? 'Ends today' : `${left} day${left === 1 ? '' : 's'} left`}
        </Chip>;
    }},
    // The tier they PAY for (Starter / Plus) — blank means Free.
    paidTier: {label: 'Paid tier', className: 'whitespace-nowrap', cell: (r) => r.paid_tier ? yesChip(r.paid_tier) : dash},
    // Online entry-fee payment taken via their registration form (Stripe).
    payments: {label: 'Payments', className: 'whitespace-nowrap', cell: (r) => r.reg_payments ? yesChip() : dash},
    // Event Passes held: bought (with what they paid), earned by signing their players up, or granted.
    passes: {label: 'Event Passes', className: 'whitespace-nowrap', cell: (r) => {
        const p = r.passes;
        if (!p || (p.paid + p.earned + p.granted) === 0) return dash;
        const paid = p.paid > 0 && new Intl.NumberFormat(undefined, {style: 'currency', currency: (p.currency || 'USD').toUpperCase()}).format(p.paid_cents / 100);
        return <span className="flex gap-1">
            {p.paid > 0 && <Chip color="success" size="sm" variant="flat">{p.paid} paid · {paid}</Chip>}
            {p.earned > 0 && <Chip color="primary" size="sm" variant="flat">{p.earned} earned</Chip>}
            {p.granted > 0 && <Chip size="sm" variant="flat">{p.granted} granted</Chip>}
        </span>;
    }},
    courtBooking: {label: 'Court booking', className: 'whitespace-nowrap', cell: (r) => r.court_booking === 'yes'
        ? <span title={r.court_booking_details || undefined}>{yesChip(`Yes${r.court_booking_details ? ' *' : ''}`)}</span>
        : r.court_booking === 'no' ? <Chip size="sm" variant="flat">No</Chip> : dash},
    geo: {label: 'Geo', className: 'whitespace-nowrap', cell: (r) => r.country_code
        ? <Chip size="sm" variant="flat">{String.fromCodePoint(...[...r.country_code.toUpperCase()].map((x) => 0x1f1a5 + x.charCodeAt(0)))} {r.country_code}</Chip>
        : dash},
} satisfies Record<string, Column>;

type ColKey = keyof typeof COLS;
/** Running trials, then finished ones, then signups whose trial never started. */
const trialRank = (r: ActivationRow) => r.trial_status === 'active' ? 0 : r.trial_status === 'finished' ? 1 : 2;

/** The page's tabs: three views of the signups, and the platform switches out of the way. */
type PageTab = 'dashboard' | 'all' | 'players' | 'trials' | 'one_off' | 'whatsapp' | 'trial_log' | 'settings';
/** Tabs with a page of their own rather than a view of the signups table. */
const OWN_PAGE: PageTab[] = ['dashboard', 'players', 'whatsapp', 'trial_log', 'settings'];
const PAGE_TABS: {key: PageTab; label: string; icon: string}[] = [
    {key: 'dashboard', label: 'Dashboard', icon: 'material-symbols-light:dashboard-outline'},
    {key: 'all', label: 'Signups', icon: 'material-symbols-light:group-outline'},
    {key: 'players', label: 'Players', icon: 'material-symbols-light:sports-and-outdoors'},
    {key: 'trials', label: 'Trials', icon: 'material-symbols-light:hourglass-top'},
    {key: 'one_off', label: 'One-off path', icon: 'material-symbols-light:confirmation-number-outline'},
    {key: 'whatsapp', label: 'WhatsApp sessions', icon: 'ic:baseline-whatsapp'},
    {key: 'trial_log', label: 'Trial & pass log', icon: 'material-symbols-light:receipt-long-outline'},
    {key: 'settings', label: 'Switches, prices & passes', icon:'material-symbols-light:toggle-on-outline'},
];

/**
 * The signups table's three views. All signups is the funnel as it always was (plus Entrants);
 * Trials and One-off path are every ORGANIZER signup in the window (players have their own tab),
 * so the ones who signed up and never started a trial, never built an event or never met the
 * pass show up as rows of dashes rather than disappearing. Trials puts running trials first,
 * soonest to end at the top — the list to work from; One-off path shows each organizer's field
 * against the free band and the Event Passes they hold.
 */
const isOrganizer = (r: ActivationRow) => r.role === 2;
const TABS: Record<Exclude<PageTab, 'dashboard' | 'players' | 'settings' | 'whatsapp' | 'trial_log'>, {label: string; hint: string; cols: ColKey[]; filter: (r: ActivationRow) => boolean; sort?: (a: ActivationRow, b: ActivationRow) => number}> = {
    all: {
        label: 'All signups', hint: '',
        cols: ['signedUp', 'user', 'role', 'source', 'geo', 'phone', 'sport', 'event', 'eventType', 'draw', 'entrants', 'created', 'players', 'selfJoined', 'scores', 'lastActive', 'cameBack', 'trial', 'paidTier', 'payments', 'courtBooking'],
        filter: () => true,
    },
    trials: {
        label: 'Trials', hint: 'Every organizer who signed up in the window — running trials first, soonest to end at the top, then finished trials, then the signups whose trial never started.',
        cols: ['signedUp', 'user', 'sport', 'event', 'entrants', 'scores', 'trialStarted', 'trialEnds', 'trialLeft', 'paidTier', 'payments', 'lastActive', 'cameBack', 'geo'],
        filter: isOrganizer,
        // Rank 2 (no trial) keeps the API's order: newest signup first.
        sort: (a, b) => trialRank(a) - trialRank(b)
            || (trialRank(a) === 0
                ? (a.trial_ends ?? '9').localeCompare(b.trial_ends ?? '9')
                : trialRank(a) === 1
                    ? (b.trial_ends ?? '').localeCompare(a.trial_ends ?? '')
                    : 0),
    },
    one_off: {
        label: 'One-off path', hint: 'Every organizer who signed up in the window, on the one-off path or not: their latest event against the free band, and the Event Passes they hold.',
        cols: ['signedUp', 'user', 'source', 'geo', 'sport', 'event', 'eventType', 'entrantsVsFree', 'draw', 'scores', 'passes', 'lastActive', 'cameBack'],
        filter: isOrganizer,
    },
};

function SignupsTable({rows, freeEntries, tab}: {rows: ActivationRow[]; freeEntries: number; tab: keyof typeof TABS}) {
    // The user whose details are open. One at a time: each opened row loads that user's passes.
    const [openUid, setOpenUid] = useState<string | null>(null);
    const ctx: CellCtx = {openUid, toggle: (uid) => setOpenUid(openUid === uid ? null : uid), freeEntries};

    const view = TABS[tab];
    const shown = rows.filter(view.filter);
    if (view.sort) shown.sort(view.sort);
    const cols = view.cols.map((k) => COLS[k] as Column);

    return <>
        {view.hint && <p className="mb-2 text-xs text-default-400">{view.hint}</p>}

        {shown.length === 0 ? (
            <div className="py-12 text-center text-default-500">
                {tab === 'all' ? 'No signups in this window.' : 'No organizer signups in this window.'}
            </div>
        ) : (
            <DualScrollTable>
                <table className={'w-full text-sm ' + (tab === 'all' ? 'min-w-[1720px]' : 'min-w-[1200px]')}>
                    <thead>
                        <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                            {cols.map((c) => <th key={c.label} className="px-3 py-2">{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r) => (
                            <React.Fragment key={r.uid}>
                                <tr className={'border-b border-default-100 last:border-0 ' + (openUid === r.uid ? 'bg-primary/5' : '')}>
                                    {cols.map((c) => <td key={c.label} className={'px-3 py-2 ' + (c.className ?? '')}>{c.cell(r, ctx)}</td>)}
                                </tr>
                                {/* Their Event Passes, and the button to give a free one — here so it
                                    does not take a trip through the (slow) Users page. Loaded only for
                                    the user that is open. */}
                                {openUid === r.uid && (
                                    <tr className="border-b border-default-100 bg-primary/5" data-activation-passes={r.uid}>
                                        <td colSpan={cols.length} className="px-3 pb-3 pt-1">
                                            <div className="max-w-[860px] rounded-lg border border-default-200 bg-content1 p-3">
                                                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-default-500">Event Passes</div>
                                                <UserEventPasses userUid={r.uid} userName={r.name || r.email || 'this user'} />
                                            </div>
                                            <div className="mt-2 max-w-[860px] rounded-lg border border-default-200 bg-content1 p-3">
                                                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-default-500">Custom pricing</div>
                                                <UserCustomPricing userUid={r.uid} userName={r.name || r.email || 'this user'} />
                                            </div>
                                            <div className="mt-2 flex max-w-[860px] items-center gap-3 rounded-lg border border-default-200 bg-content1 p-3">
                                                <SignInAsButton userUid={r.uid} name={r.name || r.email || 'this user'} />
                                                <span className="text-xs text-default-500">See ScorePal as they do, for up to an hour. Logged; password, email and deletion stay blocked.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </DualScrollTable>
        )}
    </>;
}


function RouteComponent() {
    const [days, setDays] = useState(30);
    const [page, setPage] = useState<PageTab>('all');

    const query = useQuery<{days: number; stats: {signups: number; phone_verified: number; created_event: number; created_draw: number; joined_event: number; engaged: number; returned: number; reg_paid?: number}; one_off_free_entries?: number; rows: ActivationRow[]}>({
        queryKey: ['master-activation', days],
        queryFn:  () => apiRetrieveImproved('master/activation' as any, {days}),
    });

    const stats = query.data?.stats;
    const rows = query.data?.rows || [];
    const pct = (n: number, of: number) => of > 0 ? Math.round((n / of) * 100) + '%' : '—';

    return (
        <>
            <Header />
            {/* The funnel table is wide (14 columns) — use the full screen rather than the
                narrow reading column the other master pages use. */}
            <div className="mx-auto w-[98%] py-4">
                <div className="mb-1 flex items-center justify-between gap-3">
                    <h1 className="text-2xl font-bold">Activation</h1>
                    <div className={'flex gap-1.5 ' + (page === 'settings' || page === 'trial_log' ? 'invisible' : '')}>
                        {WINDOWS.map((d) => (
                            <button key={d} type="button" onClick={() => setDays(d)}
                                    className={'rounded-full px-3 py-1 text-xs font-semibold transition ' +
                                        (d === days ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200')}>
                                {d}d
                            </button>
                        ))}
                    </div>
                </div>
                <p className="mb-1 text-sm text-default-500">
                    Every real signup in the window and how far each got — the answer to "people sign up but don't use it".
                    Shell profiles organizers imported (never claimed) are excluded.
                </p>
                <p className="mb-4 text-xs text-default-400">
                    Source <strong>G</strong> = signed up with Google. Sport / Event / Event type / Draw describe their
                    most recent event (sport falls back to their signup choice); for a league, "Draw" means its games
                    have been scheduled.
                </p>

                {/* Any user, not only this window's signups. */}
                <SignInAsSearch />

                {/* Page tabs. Each holds its own sections, so the signups table is no longer
                    buried under the switches, the funnel and two charts. */}
                <div className="mb-5 flex flex-wrap gap-1 border-b border-default-200" role="tablist">
                    {PAGE_TABS.map((t) => (
                        <button key={t.key} type="button" role="tab" aria-selected={t.key === page} onClick={() => setPage(t.key)}
                                className={'-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition ' +
                                    (t.key === page ? 'border-primary text-primary' : 'border-transparent text-default-500 hover:text-default-800')}>
                            <Icon icon={t.icon} className="text-lg" />
                            {t.label}
                            {!OWN_PAGE.includes(t.key) && !query.isLoading && (
                                <span className="rounded-full bg-default-100 px-1.5 text-xs font-medium text-default-600">
                                    {rows.filter(TABS[t.key as keyof typeof TABS].filter).length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {page === 'settings' && <>
                    <PlatformSwitches />
                    {/* Everyone's Event Pass tier prices and trial lengths; new purchases and trials only. */}
                    <PassPricesAndTrial />
                    {/* The payments v3 fee per currency, which the pricing page quotes; charges from now on. */}
                    <TransactionFees />
                    {/* For anyone, by email — the people in the Signups table have it under their name. */}
                    <GiveEventPass />
                </>}

                {/* Every organizer signup in the window: who built an event, who saw the Event
                    Pass, which route they clicked. Same window as the tab count above it. */}
                {page === 'one_off' && <OneOffFunnel days={days} />}

                {/* Every trial ever started, not just this window's signups: when each ends and
                    whether the club went on to pay. */}
                {page === 'trials' && <TrialConversionsChart />}

                {/* Every club running sessions, and how its players sign up. Its own data, not the
                    window's signups. */}
                {page === 'whatsapp' && <>
                    {/* What the messages cost, by club, over a date range — and the rates it is worked out at. */}
                    <WhatsAppSessionsCosts />
                    <WhatsAppSessionsTable />
                </>}

                {/* Platform-wide adoption: Stripe accounts, DUPR links, organisers with sign-ups. */}
                {page === 'dashboard' && <ActivationDashboard />}

                {/* The $2.99 Plus trial and the Event Pass side by side, and fee revenue by currency. Its own date filter. */}
                {page === 'trial_log' && <TrialPassLog />}

                {/* The player side of the window's signups: integrations and the paid upgrade. */}
                {page === 'players' && <PlayersActivationTable days={days} />}

                {!OWN_PAGE.includes(page) && (query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : (
                    <>
                        {page === 'all' && stats && (
                            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-8">
                                <StatCard label="Signed up" value={stats.signups} />
                                <StatCard label="Phone verified" value={stats.phone_verified} sub={pct(stats.phone_verified, stats.signups) + ' of signups'} />
                                <StatCard label="Created an event" value={stats.created_event} sub={pct(stats.created_event, stats.signups) + ' of signups'} />
                                <StatCard label="Made a draw" value={stats.created_draw} sub={pct(stats.created_draw, stats.created_event) + ' of event creators'} />
                                <StatCard label="Joined an event" value={stats.joined_event} sub={pct(stats.joined_event, stats.signups) + ' of signups'} />
                                <StatCard label="Real scores" value={stats.engaged} sub={pct(stats.engaged, stats.signups) + ' played or scored'} />
                                <StatCard label="Came back" value={stats.returned} sub={pct(stats.returned, stats.signups) + ' active after day 1'} />
                                {/* The money moment: their registration form took a real online payment. */}
                                <StatCard label="Took a payment" value={stats.reg_paid ?? 0} sub={pct(stats.reg_paid ?? 0, stats.created_event) + ' of event creators'} />
                            </div>
                        )}

                        {/* Who keeps coming back — all time, so it is not tied to this window. */}
                        {page === 'all' && <ReturnLeaderboard />}

                        <SignupsTable tab={page as keyof typeof TABS} rows={rows} freeEntries={query.data?.one_off_free_entries ?? 8} />
                    </>
                ))}
            </div>
        </>
    );
}
